import {
  Box,
  DistanceIcon,
  MarkerLocationIcon,
  NavList,
  Paragraph,
  WarningIcon,
} from '@deckee/deck-hand';
import mapboxgl from 'mapbox-gl';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../services/feature';
import {
  ROUTE_DISTANCE_TOOL,
  ROUTE_INCIDENT_REPORT,
  ROUTE_NEW_MARKER_SELECT_ENTITY_TYPE,
  ROUTE_REGULATIONS,
  ROUTE_WEATHER_SHOW,
} from '../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../services/route/route-navigation';
import {
  moveMapToLocation,
  selectDropPinLocation,
  setDropPinLocation,
  showLocationMapMarker,
  showSelectMapPoint,
} from '../../../../services/store/modules/app-controls';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import RegulationLocationCard from '../../Regulations/components/RegulationLocationCard';
import SafetyGearLocationCard from '../../Prepare/components/SafetyGearLocationCard';
import {
  defaultMapMarker,
  ReactSvg,
  stringSvg,
  weather,
} from '../../../shared/vector-images';

// Hard coded pixel size of when the app switches to mobile size
const MOBILE_BREAKPOINT = 800;

const DropPinSheet: React.FC = () => {
  const dispatch = useDispatch();
  const dropPinLocation: { latitude: number; longitude: number } = useSelector(
    selectDropPinLocation,
  );

  const theme = useTheme();
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const sheetContentRef = useRef<HTMLDivElement>(null);

  const isMobile = (window.innerWidth || 0) <= MOBILE_BREAKPOINT;

  // On mobile, place a Mapbox marker at the dropped pin location.
  // The marker stays fixed at those coordinates regardless of map panning
  // or screen resizing — it only moves when the user drops a new pin.
  useEffect(() => {
    const map = window.deckeeMap as mapboxgl.Map | undefined;
    if (!map || !dropPinLocation?.latitude || !dropPinLocation?.longitude)
      return;

    let cancelled = false;

    const setup = async () => {
      const svgString = await stringSvg(defaultMapMarker(), {
        width: 64,
        height: 64,
      });
      if (cancelled) return;

      const el = document.createElement('div');

      // Wrap the SVG in an inner div for animation. Mapbox manages the
      // outer element's transform for positioning, so animating the outer
      // element directly would override Mapbox's placement.
      const inner = document.createElement('div');
      inner.innerHTML = svgString;

      // Remove the shadow ellipse from the SVG so the pin tip sits at the
      // very bottom of the element, making anchor: 'bottom' precise.
      const shadow = inner.querySelector('ellipse');
      if (shadow) shadow.remove();

      // Crop the viewBox to the pin shape (tip at ~y=384) so there is no
      // dead space below the tip.
      const svg = inner.querySelector('svg');
      if (svg) {
        svg.setAttribute('viewBox', '0 0 400 384');
        // Prevent inline baseline gap (browsers add space below inline SVGs)
        svg.style.display = 'block';
      }

      el.appendChild(inner);

      // Remove any previous marker before adding a new one
      if (markerRef.current) {
        markerRef.current.remove();
      }

      // Ensure the marker renders above the drop pin target crosshair
      // which lives in the AppControls overlay layer.
      el.style.zIndex = '1000';

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([dropPinLocation.longitude, dropPinLocation.latitude])
        .addTo(map);

      // transform-origin bottom so scaling anchors at the pin tip
      inner.style.transformOrigin = 'center bottom';

      inner.animate(
        [
          { transform: 'translateY(-100px) scale(0.7)', opacity: 0 },
          {
            transform: 'translateY(-100px) scale(0.7)',
            opacity: 1,
            offset: 0.05,
          },
          { transform: 'translateY(0) scale(0.7)', offset: 0.55 },
          { transform: 'translateY(0) scale(1.15)', offset: 0.68 },
          { transform: 'translateY(-12px) scale(1)', offset: 0.78 },
          { transform: 'translateY(0) scale(1)', offset: 0.9 },
          { transform: 'translateY(-3px) scale(1)', offset: 0.95 },
          { transform: 'translateY(0) scale(1)' },
        ],
        { duration: 400, easing: 'ease-in', fill: 'forwards' },
      );

      // Ripple effect: a circle that expands from the pin tip and fades out
      const ripple = document.createElement('div');
      ripple.style.cssText = `
        position: absolute;
        bottom: 0;
        left: 50%;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 2px solid ${theme.colors.white}99;
        transform: translate(-50%, 50%) scale(0);
        pointer-events: none;
      `;
      el.appendChild(ripple);

      ripple.animate(
        [
          { transform: 'translate(-50%, 50%) scale(0)', opacity: 1 },
          { transform: 'translate(-50%, 50%) scale(3)', opacity: 0 },
        ],
        { duration: 600, delay: 175, easing: 'ease-out', fill: 'forwards' },
      );

      markerRef.current = marker;
    };

    setup();

    return () => {
      cancelled = true;
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [dropPinLocation, theme]);

  // Measure the sheet height after render and set a CSS custom property
  // so the DropPinTargetOverlay can position itself dynamically.
  useEffect(() => {
    if (!isMobile) return;

    const appControls = document.getElementById('appControlsHolder');

    const measureAndSet = () => {
      const sheetEl = sheetContentRef.current?.parentElement;
      if (!sheetEl || !appControls) return;
      const height = sheetEl.getBoundingClientRect().height;
      appControls.style.setProperty('--drop-pin-sheet-height', `${height}px`);
    };

    requestAnimationFrame(measureAndSet);

    return () => {
      appControls?.style.removeProperty('--drop-pin-sheet-height');
    };
  }, [isMobile]);

  // Handle initial map positioning and desktop CSS overlay
  useEffect(() => {
    if (!dropPinLocation?.latitude || !dropPinLocation?.longitude) return;

    captureEvent({
      type: 'drop-pin',
      payload: {
        clickedLatitude: dropPinLocation.latitude,
        clickLongitude: dropPinLocation.longitude,
      },
      sendToAmplitude: true,
    });

    // On mobile, offset the map so the tapped coordinates appear at the
    // visible center (above the sheet) rather than the geometric center.
    // Defer by one frame so the sheet has rendered and we can measure it.
    const positionMap = () => {
      // The map fills the full viewport (including behind the status bar),
      // so the visible map area above the sheet runs from the top of the
      // viewport (y = 0) to the top edge of the sheet. Measure the
      // sheet's actual position to center the pin in that area.
      const sheetEl = sheetContentRef.current?.parentElement;
      const sheetRect = sheetEl?.getBoundingClientRect();
      const sheetTop = sheetRect ? sheetRect.top : window.innerHeight;
      const crosshairsY = sheetTop / 2;
      const mobileOffsetY = window.innerHeight / 2 - crosshairsY;

      dispatch(
        moveMapToLocation({
          center: {
            lat: dropPinLocation.latitude,
            lon: dropPinLocation.longitude,
          },
          offsetY: isMobile ? mobileOffsetY : 0,
        }),
      );
    };

    if (isMobile) {
      requestAnimationFrame(positionMap);
    } else {
      positionMap();
    }
    dispatch(showSelectMapPoint(0));
  }, [dispatch, dropPinLocation, isMobile]);

  // On mount, clear any map markers/overlays left by other routes.
  // The drop-pin route has no layout manager action, so stale state
  // persists when navigating back:
  // - MarkerCreateSelectEntityType sets showingLocationMapMarker: true
  // - NewMarkerSetLocation sets showSelectMapPoint(-1)
  // We clear these specifically rather than using setBaseMap() which
  // would also reset showingPageHolder and break the sheet rendering.
  useEffect(() => {
    dispatch(showSelectMapPoint(0));
    dispatch(showLocationMapMarker(null));
    return () => {
      dispatch(showSelectMapPoint(0));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMeasureDistanceClick = async () => {
    await captureEvent({
      type: 'drop-pin: measure distance',
      payload: {},
      sendToAmplitude: true,
    });
    transitionToNextRoute(ROUTE_DISTANCE_TOOL);
  };

  const onIncidentReportClick = async () => {
    await captureEvent({
      type: 'drop-pin: report incident',
      payload: {},
      sendToAmplitude: true,
    });
    transitionToNextRoute(ROUTE_INCIDENT_REPORT);
  };

  const onShowForecastClick = async () => {
    await captureEvent({
      type: 'drop-pin: view forecast',
      payload: {},
      sendToAmplitude: true,
    });
    transitionToNextRoute(ROUTE_WEATHER_SHOW, {
      latitude: dropPinLocation.latitude,
      longitude: dropPinLocation.longitude,
    });
  };

  return (
    <SheetLayout
      maxViewHeight="35"
      showMenuBar={false}
      heading="Dropped pin"
      sheetRef={sheetContentRef}
    >
      <SheetBody pb="0" overflow="auto">
        <NavList>
          <NavList.Item
            onClick={() => {
              dispatch(
                setDropPinLocation({
                  latitude: dropPinLocation.latitude,
                  longitude: dropPinLocation.longitude,
                }),
              );
              transitionToNextRoute(ROUTE_NEW_MARKER_SELECT_ENTITY_TYPE, {
                lat: dropPinLocation.latitude,
                lon: dropPinLocation.longitude,
              });
            }}
            chevron
          >
            <MarkerLocationIcon color={theme.colors.primary} />
            <Box>
              <Paragraph>Add new marker</Paragraph>
            </Box>
          </NavList.Item>
          <SafetyGearLocationCard
            showLatLon={false}
            latitude={dropPinLocation?.latitude}
            longitude={dropPinLocation?.longitude}
          />
          <RegulationLocationCard
            onClick={() => {
              dispatch(
                setDropPinLocation({
                  latitude: dropPinLocation.latitude,
                  longitude: dropPinLocation.longitude,
                }),
              );
              transitionToNextRoute(ROUTE_REGULATIONS, {
                latitude: dropPinLocation?.latitude,
                longitude: dropPinLocation?.longitude,
              });
            }}
            showLatLon={false}
            latitude={dropPinLocation?.latitude}
            longitude={dropPinLocation?.longitude}
          />
          {hasFeatureEnabled('incident-reporting') && (
            <NavList.Item chevron onClick={onIncidentReportClick}>
              <WarningIcon color={theme.colors.primary} />
              <Paragraph>Report an incident</Paragraph>
            </NavList.Item>
          )}

          <NavList.Item chevron onClick={onShowForecastClick}>
            <ReactSvg
              toDisplay={weather()}
              options={{
                fillColor: theme.colors.primary,
              }}
              width={24}
              height={24}
            />
            <Paragraph>View forecast</Paragraph>
          </NavList.Item>

          <NavList.Item chevron onClick={onMeasureDistanceClick}>
            <DistanceIcon color={theme.colors.primary} />
            <Paragraph>Measure distance</Paragraph>
          </NavList.Item>
        </NavList>
      </SheetBody>
    </SheetLayout>
  );
};
export default DropPinSheet;
